import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { AppHeader } from "@/components/AppHeader";
import { AdminLegalAcceptanceSummary } from "@/components/LegalAcceptanceStatus";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck, UserCheck, Clock, Mail as MailIcon, Phone as PhoneIcon, CheckCircle2, Briefcase, Wrench, Crown, UserCog, Trash2, AlertTriangle, EyeOff, RotateCcw, PowerOff, ScrollText, Sparkles, FlaskConical, Eraser, Table as TableIcon, UserPlus, X as XIcon, X, Loader2 as Loader2Icon, Save as SaveIcon, Building2, Pencil, Activity as ActivityIcon, Sliders, Users, Copy as CopyIcon } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import AdminEditUserModal from "@/components/AdminEditUserModal";
import PermissionsEditor from "@/components/PermissionsEditor";
import MembershipDialog from "@/components/MembershipDialog";

const ROLE_LABEL = {
  admin: "Administrator",
  sales_director: "Vertriebsdirektor",
  sales_manager: "Vertriebsleiter",
  branch_manager: "Niederlassungsleiter",
  sales_rep: "Vertrieb",
  telesales_director: "Telesales-Direktor",
  agent: "Telesales-Agent",
  installer: "Installation",
};
const ROLE_OPTIONS = ["agent", "telesales_director", "sales_rep", "sales_manager", "sales_director", "installer", "admin"];
const STATUSES = ["pending", "active", "blocked"];

const roleIcon = (r) => {
  if (r === "admin") return <Crown size={14} />;
  if (r === "sales_director") return <Crown size={14} />;
  if (r === "sales_manager") return <UserCog size={14} />;
  if (r === "installer") return <Wrench size={14} />;
  return <Briefcase size={14} />;
};

export default function AdminUsers() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get("focus");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Iter89r (2026-02-24) — Pending Einladungen (Magic-Link, noch nicht angenommen)
  const [pendingInvites, setPendingInvites] = useState([]);
  const [revokingInviteId, setRevokingInviteId] = useState(null);
  const [resendingInviteId, setResendingInviteId] = useState(null);
  const [autoApprove, setAutoApprove] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, name, email, ...}
  const [resetTarget, setResetTarget] = useState(null); // {id, name, ...} — for test data reset
  const [editTarget, setEditTarget] = useState(null); // {id, ...user} — admin profile edit
  const [permsTarget, setPermsTarget] = useState(null); // {id, ...user} — permissions editor
  const [membershipsTarget, setMembershipsTarget] = useState(null); // {id, ...user} — tenant memberships editor
  const [bulkTarget, setBulkTarget] = useState(null); // Iter88x — Manager for bulk apply
  const [sendingReport, setSendingReport] = useState(false); // Iter88y — DSGVO-Monatsreport-Versand

  const sendAuditReport = async () => {
    if (!window.confirm("Rechte-Änderungs-Monatsreport an alle Direktoren/Admins deines Mandanten senden? (Vormonat)")) return;
    setSendingReport(true);
    try {
      const r = await api.post("/admin/permission-audit-report/send", {});
      const to = r.data?.sent_to || [];
      toast.success(`DSGVO-Report versendet an ${to.length} Empfänger (${r.data?.entries_count || 0} Änderungen)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Report-Versand fehlgeschlagen");
    } finally {
      setSendingReport(false);
    }
  };
  const [showDeleted, setShowDeleted] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", role: "agent", tenant_id: "",
  });
  const [createSaving, setCreateSaving] = useState(false);

  // Super-Admin in Global Mode (kein Acting-Tenant): braucht inline Tenant-Auswahl
  const isSuperAdmin = !!currentUser?.is_super_admin;
  const actingTenantId = (typeof window !== "undefined")
    ? localStorage.getItem("novatus_acting_tenant_id") || ""
    : "";
  const needsTenantPick = isSuperAdmin && !actingTenantId;
  const [tenantOptions, setTenantOptions] = useState([]);
  // Iter88w: tenant-map (id → name/slug) IMMER laden, damit die Mandanten-Chips
  // in der User-Liste (auch für Non-Global-Modus) sichtbar sind.
  const tenantMap = useMemo(() => {
    const m = {};
    for (const t of tenantOptions) m[t.id] = t;
    return m;
  }, [tenantOptions]);

  // Lade Tenant-Liste einmal — für Global-Mode zur User-Anlage UND für die
  // Mandanten-Chip-Anzeige in der Liste. Endpoint ist nur für Super-Admin;
  // für andere fällt es sanft aus (`.catch → []`).
  useEffect(() => {
    api.get("/superadmin/tenants")
      .then((r) => setTenantOptions((r.data || []).filter((t) => t.active !== false)))
      .catch(() => setTenantOptions([]));
  }, []);

  // Allowed-roles (Hierarchical permission)
  const [allowedRoles, setAllowedRoles] = useState(ROLE_OPTIONS);
  useEffect(() => {
    api.get("/admin/allowed-roles")
      .then((r) => {
        const arr = r.data?.allowed_roles?.length ? r.data.allowed_roles : ROLE_OPTIONS;
        setAllowedRoles(arr);
        // If current selected role is not in allowed → reset to first allowed
        setCreateForm((cf) => arr.includes(cf.role) ? cf : { ...cf, role: arr[0] });
      })
      .catch(() => setAllowedRoles(ROLE_OPTIONS));
  }, []);

  // Invite-by-Link state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ role: "agent", target_email: "", target_phone: "", first_name: "", last_name: "", tenant_id: "" });
  const [inviteSaving, setInviteSaving] = useState(false);
  const [lastInviteUrl, setLastInviteUrl] = useState("");
  const submitInvite = async () => {
    if (!inviteForm.target_email && !inviteForm.target_phone) {
      toast.error("E-Mail oder Telefon angeben"); return;
    }
    if (needsTenantPick && !inviteForm.tenant_id) {
      toast.error("Bitte einen Mandanten auswählen (Sie sind im Global-Modus).");
      return;
    }
    setInviteSaving(true);
    try {
      // Im Global-Modus tenant_id mitsenden; sonst weglassen (Backend nutzt Header/eigenen Tenant)
      const payload = needsTenantPick ? inviteForm : { ...inviteForm, tenant_id: undefined };
      const { data } = await api.post("/admin/invitations", payload);
      toast.success(data.delivery?.email?.ok ? "Einladung versendet" : "Einladung erstellt — Link unten");
      setLastInviteUrl(data.invite_url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Einladung fehlgeschlagen");
    } finally { setInviteSaving(false); }
  };

  const submitCreate = async () => {
    const f = createForm;
    if (!f.first_name || !f.last_name || !f.email || !f.phone) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return;
    }
    if (needsTenantPick && !f.tenant_id) {
      toast.error("Bitte einen Mandanten auswählen (Sie sind im Global-Modus).");
      return;
    }
    setCreateSaving(true);
    try {
      // Im Global-Modus tenant_id mitsenden; sonst weglassen (Backend nutzt Header/eigenen Tenant)
      const payload = needsTenantPick ? f : { ...f, tenant_id: undefined };
      const { data } = await api.post("/admin/users", payload);
      toast.success(`User ${data.first_name} ${data.last_name} angelegt (${data.role})`);
      setCreating(false);
      setCreateForm({ first_name: "", last_name: "", email: "", phone: "", role: "agent", tenant_id: "" });
      load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen");
    } finally { setCreateSaving(false); }
  };

  const load = (silent = false) => {
    if (!silent) setLoading(true);
    const params = showDeleted ? "?include_deleted=true" : "";
    api.get(`/users${params}`).then((r) => setUsers(r.data)).finally(() => { if (!silent) setLoading(false); });
    api.get("/admin/settings")
      .then((r) => setAutoApprove(r.data.auto_approve !== false))
      .catch(() => {});
    // Iter89r (2026-02-24) — offene Einladungen (Magic-Link, nicht abgelaufen,
    // nicht angenommen). Iter89s: zusätzlich ABGELAUFENE anzeigen, damit
    // Admin diese per „Nochmal senden" reaktivieren kann.
    api.get("/admin/invitations")
      .then((r) => {
        const all = Array.isArray(r.data) ? r.data : [];
        const open = all.filter((i) => i.status === "pending" || i.status === "expired");
        setPendingInvites(open);
      })
      .catch(() => setPendingInvites([]));
  };
  useEffect(load, [showDeleted]);  // eslint-disable-line react-hooks/exhaustive-deps
  // Iter88zbu — Silent-Reload nach jeder Mutation, kein Full-Page-Spinner-Flash.
  useReloadBus("*", () => load(true));

  // Iter89r (2026-02-24) — Einladungs-Actions: Link kopieren + Revoke
  const copyInviteLink = async (inv) => {
    const url = inv.invite_url || "";
    if (!url) { toast.error("Kein Link an dieser Einladung"); return; }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Einladungs-Link in Zwischenablage");
    } catch {
      window.prompt("Einladungs-Link kopieren:", url);
    }
  };
  const revokeInvite = async (inv) => {
    const target = inv.target_email || inv.target_phone || inv.email;
    if (!window.confirm(`Einladung an ${target} wirklich zurückziehen?`)) return;
    setRevokingInviteId(inv.id);
    try {
      await api.post(`/admin/invitations/${inv.id}/revoke`);
      toast.success("Einladung zurückgezogen");
      load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zurückziehen fehlgeschlagen");
    } finally {
      setRevokingInviteId(null);
    }
  };

  // Iter89s (2026-02-24) — Einladung erneut zusenden.
  const resendInvite = async (inv) => {
    const target = inv.target_email || inv.target_phone;
    if (!window.confirm(`Einladung erneut an ${target} senden? Ein neuer Link wird erzeugt (der alte wird ungültig).`)) return;
    setResendingInviteId(inv.id);
    try {
      const { data } = await api.post(`/admin/invitations/${inv.id}/resend`);
      const emailStatus = data?.delivery?.email?.status;
      if (emailStatus === "sent") {
        toast.success(`Einladung erneut an ${target} verschickt (Nr. ${data.resend_count})`);
      } else if (emailStatus) {
        toast.warning(`Erneut gesendet — Mail-Status: ${emailStatus}`);
      } else {
        toast.success("Einladung erneut generiert — Link neu in Zwischenablage");
        try { await navigator.clipboard.writeText(data.invite_url || ""); } catch {}
      }
      load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erneut senden fehlgeschlagen");
    } finally {
      setResendingInviteId(null);
    }
  };

  // Auto-expand and scroll-to the user referenced via ?focus=<id>
  useEffect(() => {
    if (!focusId || users.length === 0) return;
    if (users.some((u) => u.id === focusId)) {
      setExpandedUser(focusId);
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="admin-user-${focusId}"]`)
          || document.querySelector(`[data-testid="pending-user-${focusId}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
  }, [focusId, users]);

  const toggleAutoApprove = async () => {
    const next = !autoApprove;
    try {
      await api.patch("/admin/settings/auto_approve", { enabled: next });
      setAutoApprove(next);
      toast.success(`Auto-Freischaltung ${next ? "AKTIVIERT" : "DEAKTIVIERT"}`);
    } catch (e) {
      toast.error("Fehler beim Umschalten");
    }
  };

  const update = async (userId, payload) => {
    try {
      await api.patch(`/users/${userId}`, payload);
      toast.success("Benutzer aktualisiert");
      load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const approve = async (userId) => {
    try {
      await api.patch(`/users/${userId}`, { status: "active" });
      toast.success("Konto freigeschaltet — Bestätigungs-E-Mail versendet");
      load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Freischaltung fehlgeschlagen");
    }
  };

  const confirmDelete = (u) => {
    setDeleteTarget({
      id: u.id,
      name: `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || u.id,
      email: u.email,
      phone: u.phone,
      role: u.role,
      status: u.status,
    });
  };

  const performDelete = async () => {
    if (!deleteTarget) return;
    try {
      const r = await api.delete(`/users/${deleteTarget.id}`);
      toast.success("Benutzer endgültig gelöscht", {
        description: r.data?.email || deleteTarget.name,
      });
      setDeleteTarget(null);
      load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const performSoftDelete = async () => {
    if (!deleteTarget) return;
    try {
      const r = await api.post(`/users/${deleteTarget.id}/soft-delete`);
      toast.success("Benutzer deaktiviert", {
        description: `${r.data?.name || deleteTarget.name} kann jederzeit wiederhergestellt werden.`,
      });
      setDeleteTarget(null);
      load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Deaktivierung fehlgeschlagen");
    }
  };

  const restoreUser = async (u) => {
    try {
      const r = await api.post(`/users/${u.id}/restore`);
      toast.success("Benutzer wiederhergestellt", {
        description: `Status: ${r.data?.restored_to_status || "blocked"}`,
      });
      load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Wiederherstellung fehlgeschlagen");
    }
  };

  const toggleTestAccount = async (u, isTest) => {
    try {
      await api.patch(`/users/${u.id}`, { is_test_account: isTest });
      toast.success(
        isTest
          ? "Als Test-Konto markiert — Daten können jetzt zurückgesetzt werden"
          : "Test-Konto-Markierung entfernt"
      );
      load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Umschalten");
    }
  };

  const performResetTestData = async () => {
    if (!resetTarget) return;
    try {
      const r = await api.post(`/users/${resetTarget.id}/reset-test-data`);
      const d = r.data?.deleted || {};
      const counts = Object.entries(d)
        .filter(([k]) => !k.endsWith("_error"))
        .map(([k, v]) => `${k}: ${v}`)
        .join(", ");
      toast.success("Test-Daten zurückgesetzt", {
        description: counts || `${r.data?.project_count || 0} Projekte entfernt`,
      });
      setResetTarget(null);
      load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset fehlgeschlagen");
    }
  };

  const managers = users.filter((u) => u.role === "sales_manager" && u.status === "active");
  // Iter88zbw — Niederlassungen für den Row-Editor laden. Ein GET reicht,
  // da Auto-Reload via Interceptor bei Save weitergeführt wird.
  const [branches, setBranches] = useState([]);
  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => setBranches([]));
  }, []);
  useReloadBus("*", () => {
    api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {});
  });
  // Vertriebsdirektoren für die Sales-Manager-Zuordnung (n:1 — viele Vertriebsleiter
  // pro Vertriebsdirektor möglich, klargestellt am 26.06.2026).
  const directors = users.filter((u) => u.role === "sales_director" && u.status === "active");
  // Vorgesetzte für Telesales-Direktoren (klargestellt 27.06.2026): Vertriebsdirektor
  // oder Admin/Super-Admin.
  const tsdSupervisors = users.filter((u) =>
    ["sales_director", "admin"].includes(u.role) && u.status === "active",
  );
  // Mögliche Vorgesetzte für Telesales-Agents: Vertriebsleiter, -Direktor,
  // Telesales-Direktor, Admin (sales_rep NICHT, gem. User-Request 26.06.2026).
  const agentSupervisors = users.filter((u) =>
    ["sales_manager", "sales_director", "admin", "telesales_director"].includes(u.role)
    && u.status === "active",
  );
  const pending = useMemo(() => users.filter((u) => u.status === "pending"), [users]);
  const deleted = useMemo(() => users.filter((u) => u.status === "deleted"), [users]);
  const others = useMemo(() => users.filter((u) => u.status !== "pending" && u.status !== "deleted"), [users]);

  return (
    <div className="min-h-screen bg-[#09090B]">
      <AppHeader title="Benutzerverwaltung" back="/dashboard" />
      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <div className="h-12 w-12 rounded-2xl bg-[#00E575]/15 flex items-center justify-center"><ShieldCheck className="text-[#00E575]" /></div>
          <div className="flex-1 min-w-[200px]">
            <h1 className="font-display text-2xl font-semibold">Benutzer & Rollen</h1>
            <p className="text-sm text-zinc-300 font-medium">Rollen, Status und Vertriebsleiter-Zuordnung verwalten</p>
          </div>
          {pending.length > 0 && (
            <div data-testid="admin-pending-counter" className="px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-xs font-bold uppercase tracking-wider">
              {pending.length} ausstehend
            </div>
          )}
          {pendingInvites.length > 0 && (
            <div data-testid="admin-pending-invites-counter" className="px-3 py-1.5 rounded-full bg-sky-500/15 border border-sky-500/40 text-sky-300 text-xs font-bold uppercase tracking-wider">
              {pendingInvites.length} offene Einladung{pendingInvites.length === 1 ? "" : "en"}
            </div>
          )}
          <button
            data-testid="admin-create-user-btn"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#00E575] text-[#09090B] text-xs font-bold hover:bg-[#00C964]"
          >
            <UserPlus size={14} /> Neuer User
          </button>
          <button
            data-testid="admin-send-audit-report-btn"
            onClick={sendAuditReport}
            disabled={sendingReport}
            title="Rechte-Änderungs-Monatsreport per E-Mail (Vormonat, DSGVO-Nachweis)"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-200 hover:bg-amber-500/25 text-xs font-bold disabled:opacity-50"
          >
            {sendingReport ? <Loader2Icon size={13} className="animate-spin"/> : <ScrollText size={13}/>}
            DSGVO-Report
          </button>
          <a
            data-testid="admin-presence-link"
            href="/admin/presence"
            title="Live-Übersicht: Wer ist online? Wann waren Benutzer aktiv?"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs font-bold hover:border-[#00E575]/40 hover:text-[#00E575]"
          >
            <ActivityIcon size={14} /> Live-Übersicht
          </a>
          <button
            data-testid="admin-invite-user-btn"
            onClick={() => { setInviteOpen(true); setLastInviteUrl(""); }}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-300 text-xs font-bold hover:bg-amber-500/25"
          >
            <MailIcon size={14} /> Einladen per Link
          </button>
          <a
            data-testid="admin-key-facts-link"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/public/key-facts.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            title="Key-Facts-Übersicht der App als 10-Seiten-PDF"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#00E575]/10 border border-[#00E575]/40 text-[#00E575] text-xs font-semibold hover:bg-[#00E575]/20 transition"
          >
            <Sparkles size={13} /> Key Facts
          </a>
          <button
            data-testid="admin-users-table-btn"
            onClick={() => navigate("/admin/users-table")}
            title="Vollständige Daten-Tabelle mit Sortierung und KPIs"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-sky-500/10 border border-sky-500/40 text-sky-200 text-xs font-semibold hover:bg-sky-500/20 transition"
          >
            <TableIcon size={13} /> Tabelle
          </button>
          <button
            data-testid="admin-audit-log-btn"
            onClick={() => navigate("/admin/audit-log")}
            title="Chronologisches Audit-Log aller Admin-Aktionen"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/40 text-violet-200 text-xs font-semibold hover:bg-violet-500/20 transition"
          >
            <ScrollText size={13} /> Audit-Log
          </button>
          <button
            data-testid="admin-test-data-btn"
            onClick={() => navigate("/admin/test-data")}
            title="Test-Daten-Manager: Test-Konten, Test-Projekte und Test-Submissions kontrolliert löschen"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/40 text-red-200 text-xs font-semibold hover:bg-red-500/20 transition"
          >
            <Eraser size={13} /> Test-Daten
          </button>
          <button
            data-testid="admin-modules-btn"
            onClick={() => navigate("/admin/modules")}
            title="Modul-Verwaltung: Telesales / PV / Lead-Auctions an- und ausschalten"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#00E575]/10 border border-[#00E575]/40 text-[#00E575] text-xs font-semibold hover:bg-[#00E575]/20 transition"
          >
            <Sliders size={13} /> Module
          </button>
          <button
            data-testid="admin-toggle-show-deleted"
            onClick={() => setShowDeleted((v) => !v)}
            title={showDeleted ? "Gelöschte/deaktivierte Konten ausblenden" : "Auch deaktivierte/soft-gelöschte Konten anzeigen"}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${showDeleted ? "bg-zinc-700 text-zinc-100 border-zinc-600" : "bg-zinc-900 text-zinc-300 border-zinc-700 hover:bg-zinc-800"}`}
          >
            <EyeOff size={13} /> {showDeleted ? "Gelöschte verbergen" : "Gelöschte anzeigen"}
            {showDeleted && deleted.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-500/30 text-red-200 text-[12px] font-bold">
                {deleted.length}
              </span>
            )}
          </button>
        </div>

        {/* Auto-approve toggle */}
        <div
          data-testid="admin-auto-approve-toggle"
          className={`mb-6 px-4 py-3 rounded-xl border flex items-center justify-between gap-3 ${autoApprove ? "bg-[#00E575]/5 border-[#00E575]/30" : "bg-zinc-800/40 border-zinc-700"}`}
        >
          <div className="min-w-0">
            <div className="text-sm font-semibold flex items-center gap-2">
              <span className={autoApprove ? "text-[#00E575]" : "text-zinc-400"}>●</span>
              Auto-Freischaltung
            </div>
            <p className="text-xs text-zinc-300 mt-0.5 leading-snug">
              {autoApprove
                ? "Aktiviert — Plausible Registrierungen (100/100) werden automatisch freigeschaltet."
                : "Deaktiviert — Jeder Account muss manuell freigeschaltet werden."}
            </p>
          </div>
          <button
            data-testid="admin-toggle-auto-approve-btn"
            onClick={toggleAutoApprove}
            className={`h-9 px-4 rounded-full text-xs font-bold uppercase tracking-wider transition ${autoApprove ? "bg-[#00E575] text-[#09090B] hover:bg-[#00C964]" : "bg-zinc-700 text-zinc-300 hover:bg-zinc-600"}`}
          >
            {autoApprove ? "Aktiviert" : "Deaktiviert"}
          </button>
        </div>

        {loading ? <div className="text-zinc-300 text-center font-medium py-12">Lade…</div> : (
          <>
            {/* ===== OFFENE EINLADUNGEN (Magic-Link, nicht angenommen) =====
                Iter89r (2026-02-24) — User-Wunsch: Doppeleinladungen vermeiden */}
            {pendingInvites.length > 0 && (
              <section data-testid="admin-pending-invites-section" className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <MailIcon className="text-sky-400" size={18} />
                  <h2 className="font-display text-lg font-semibold text-sky-200">
                    Offene Einladungen (Link versendet, Benutzer hat noch nicht angenommen)
                  </h2>
                </div>
                <div className="space-y-2">
                  {pendingInvites.map((inv) => {
                    const expiresAt = inv.expires_at ? new Date(inv.expires_at) : null;
                    const isExpired = expiresAt ? expiresAt.getTime() < Date.now() : false;
                    const createdAt = inv.created_at ? new Date(inv.created_at) : null;
                    return (
                      <div
                        key={inv.id}
                        data-testid={`pending-invite-${inv.id}`}
                        className={`rounded-2xl p-4 border-2 ${
                          isExpired
                            ? "bg-red-500/5 border-red-500/40"
                            : "bg-sky-500/5 border-sky-500/40"
                        }`}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                          <div className="md:col-span-5 min-w-0">
                            <div className="font-semibold text-zinc-50 truncate flex items-center gap-2 flex-wrap">
                              {inv.first_name || inv.last_name
                                ? `${inv.first_name || ""} ${inv.last_name || ""}`.trim()
                                : <span className="text-zinc-300">(Name nicht erfasst)</span>}
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-zinc-800 text-zinc-200 uppercase tracking-wider">
                                {roleIcon(inv.role)} {ROLE_LABEL[inv.role] || inv.role}
                              </span>
                              {isExpired ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-red-500/15 text-red-300 uppercase tracking-wider">
                                  Abgelaufen
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-sky-500/15 text-sky-300 uppercase tracking-wider">
                                  Pending
                                </span>
                              )}
                              {Number(inv.resend_count) > 0 && (
                                <span
                                  data-testid={`pending-invite-resend-count-${inv.id}`}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-300 uppercase tracking-wider"
                                  title={inv.last_resent_at ? `Zuletzt neu verschickt: ${new Date(inv.last_resent_at).toLocaleString("de-DE")}` : "Erneut verschickt"}
                                >
                                  {inv.resend_count}× erneut
                                </span>
                              )}
                              {/* Iter89t (2026-02-24) — Open-Tracking-Badge.
                                  Zeigt, OB die Einladungsmail bereits geöffnet wurde. */}
                              {Number(inv.open_count) > 0 ? (
                                <span
                                  data-testid={`pending-invite-opened-${inv.id}`}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-fuchsia-500/15 text-fuchsia-200 uppercase tracking-wider"
                                  title={`Zuletzt geöffnet: ${inv.last_opened_at ? new Date(inv.last_opened_at).toLocaleString("de-DE") : "–"} · ${inv.open_count}× geöffnet · UA: ${inv.last_opened_user_agent || "unbekannt"}`}
                                >
                                  👁 {inv.open_count}× geöffnet
                                </span>
                              ) : (
                                <span
                                  data-testid={`pending-invite-unopened-${inv.id}`}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-zinc-700/60 text-zinc-300 uppercase tracking-wider"
                                  title="Noch nicht geöffnet (oder Mail-Client blockiert externe Bilder)"
                                >
                                  nicht geöffnet
                                </span>
                              )}
                            </div>
                            <div className="text-[13px] text-zinc-300 font-mono mt-0.5 truncate" data-testid={`pending-invite-email-${inv.id}`}>
                              {inv.target_email || inv.target_phone || "(ohne Kontakt)"}
                            </div>
                            {inv.target_phone && inv.target_email && (
                              <div className="text-[12px] text-zinc-300 font-mono mt-0.5 truncate">
                                {inv.target_phone}
                              </div>
                            )}
                          </div>
                          <div className="md:col-span-4 text-[12.5px] text-zinc-300 space-y-0.5">
                            {createdAt && (
                              <div>
                                <span className="text-zinc-400">Versendet: </span>
                                <span className="text-zinc-100 font-medium">
                                  {createdAt.toLocaleString("de-DE", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" })}
                                </span>
                              </div>
                            )}
                            {expiresAt && (
                              <div>
                                <span className="text-zinc-400">Gültig bis: </span>
                                <span className={`font-medium ${isExpired ? "text-red-300" : "text-zinc-100"}`}>
                                  {expiresAt.toLocaleString("de-DE", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" })}
                                </span>
                              </div>
                            )}
                            {inv.inviter_name && (
                              <div>
                                <span className="text-zinc-400">Eingeladen durch: </span>
                                <span className="text-zinc-100 font-medium">{inv.inviter_name}</span>
                              </div>
                            )}
                            {inv.first_opened_at && (
                              <div data-testid={`pending-invite-first-opened-${inv.id}`}>
                                <span className="text-zinc-400">Erstmals geöffnet: </span>
                                <span className="text-fuchsia-200 font-medium">
                                  {new Date(inv.first_opened_at).toLocaleString("de-DE", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" })}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="md:col-span-3 flex flex-wrap gap-1.5 justify-end">
                            {inv.invite_url && !isExpired && (
                              <button
                                onClick={() => copyInviteLink(inv)}
                                data-testid={`pending-invite-copy-${inv.id}`}
                                className="h-9 px-3 rounded-lg bg-sky-500/15 text-sky-200 border border-sky-500/40 hover:bg-sky-500/25 text-[12px] font-semibold inline-flex items-center gap-1.5"
                                title="Einladungs-Link in Zwischenablage"
                              >
                                <CopyIcon size={13} /> Link
                              </button>
                            )}
                            <button
                              onClick={() => resendInvite(inv)}
                              disabled={resendingInviteId === inv.id}
                              data-testid={`pending-invite-resend-${inv.id}`}
                              className="h-9 px-3 rounded-lg bg-emerald-500/15 text-emerald-200 border border-emerald-500/40 hover:bg-emerald-500/25 disabled:opacity-50 text-[12px] font-semibold inline-flex items-center gap-1.5"
                              title={isExpired ? "Neuen Link erzeugen & erneut senden" : "Erneut senden — generiert neuen Link"}
                            >
                              {resendingInviteId === inv.id
                                ? <Loader2Icon size={13} className="animate-spin"/>
                                : <><RotateCcw size={13}/> Nochmal senden</>}
                            </button>
                            <button
                              onClick={() => revokeInvite(inv)}
                              disabled={revokingInviteId === inv.id}
                              data-testid={`pending-invite-revoke-${inv.id}`}
                              className="h-9 px-3 rounded-lg bg-red-500/10 text-red-300 border border-red-500/30 hover:bg-red-500/20 disabled:opacity-50 text-[12px] font-semibold inline-flex items-center gap-1.5"
                              title="Einladung zurückziehen"
                            >
                              {revokingInviteId === inv.id
                                ? <Loader2Icon size={13} className="animate-spin"/>
                                : <><X size={13}/> Widerrufen</>}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* ===== PENDING SECTION ===== */}
            {pending.length > 0 && (
              <section data-testid="admin-pending-section" className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="text-amber-400" size={18} />
                  <h2 className="font-display text-lg font-semibold text-amber-200">
                    Neue Anmeldungen warten auf Freischaltung
                  </h2>
                </div>
                <div className="space-y-2">
                  {pending.map((u) => (
                    <div
                      key={u.id}
                      data-testid={`pending-user-${u.id}`}
                      className="bg-amber-500/5 border-2 border-amber-500/40 rounded-2xl p-4"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                        <div className="md:col-span-5 min-w-0">
                          <div className="font-semibold text-zinc-50 truncate flex items-center gap-2 flex-wrap">
                            {u.first_name} {u.last_name}
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-zinc-800 text-zinc-200 uppercase tracking-wider">
                              {roleIcon(u.role)} {ROLE_LABEL[u.role] || u.role}
                            </span>
                          </div>
                          <div className="text-sm text-zinc-200 truncate mt-1 flex items-center gap-1.5">
                            <MailIcon size={12} className="text-zinc-400" /> {u.email}
                            {u.email_confirmed_at ? (
                              <span className="inline-flex items-center gap-0.5 text-[#00E575] text-xs font-bold">
                                <CheckCircle2 size={11} /> bestätigt
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-0.5 text-amber-400 text-xs font-bold uppercase">
                                · wartet auf E-Mail-Bestätigung
                              </span>
                            )}
                          </div>
                          {u.phone && (
                            <div className="text-sm text-zinc-300 font-mono mt-0.5 flex items-center gap-1.5">
                              <PhoneIcon size={12} className="text-zinc-400" /> {u.phone}
                              {u.phone_verified && (
                                <span className="inline-flex items-center gap-0.5 text-[#00E575] text-xs font-bold">
                                  <CheckCircle2 size={11} /> verifiziert
                                </span>
                              )}
                            </div>
                          )}
                          {/* Profile details */}
                          {(u.street || u.zip || u.city) && (
                            <div className="text-sm text-zinc-300 mt-1.5 leading-snug">
                              {u.street} {u.house_number}<br />
                              {u.zip} {u.city}
                            </div>
                          )}
                          {u.iban && (
                            <div className="text-xs text-zinc-400 font-mono mt-1 truncate">
                              IBAN: {u.iban}
                            </div>
                          )}
                          {(u.company_name || u.vat_id) && (
                            <div className="text-xs text-zinc-400 mt-1">
                              {u.company_name && <>🏢 {u.company_name}</>}
                              {u.vat_id && <> · USt-ID: <span className="font-mono">{u.vat_id}</span></>}
                            </div>
                          )}
                          {u.created_at && (
                            <div className="text-xs text-zinc-400 mt-2">
                              Eingang: {new Date(u.created_at).toLocaleString("de-DE")}
                            </div>
                          )}
                          <AdminLegalAcceptanceSummary acceptances={u.legal_acceptances} />
                        </div>
                        <div className="md:col-span-3">
                          {(() => {
                            const isAgent = u.role === "agent";
                            const isRep   = u.role === "sales_rep";
                            const isMgr   = u.role === "sales_manager";
                            const isTsd   = u.role === "telesales_director";
                            const enabled = isAgent || isRep || isMgr || isTsd;
                            const label =
                              isTsd   ? "Vorgesetzter"      :
                              isMgr   ? "Vertriebsdirektor" :
                              isAgent ? "Vorgesetzter"      : "Vertriebsleiter";
                            // Beide Felder am User möglich; wir nutzen
                            // sales_director_id für Manager+Telesales-Director,
                            // sales_manager_id für Rep+Agent (existierende Felder).
                            const fieldKey =
                              isMgr || isTsd ? "sales_director_id" : "sales_manager_id";
                            const optList =
                              isTsd   ? tsdSupervisors   :
                              isMgr   ? directors        :
                              isAgent ? agentSupervisors : managers;
                            return (
                              <>
                                <label className="block text-xs uppercase tracking-widest text-zinc-400 mb-1">
                                  {label}
                                </label>
                                <select
                                  data-testid={`pending-manager-${u.id}`}
                                  value={u[fieldKey] || ""}
                                  onChange={(e) => update(u.id, { [fieldKey]: e.target.value || null })}
                                  className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 text-sm"
                                  disabled={!enabled}
                                >
                                  <option value="">– kein –</option>
                                  {optList.map((m) => (
                                    <option key={m.id} value={m.id}>
                                      {m.first_name} {m.last_name}{(isAgent || isTsd) ? ` · ${m.role}` : ""}
                                    </option>
                                  ))}
                                </select>
                              </>
                            );
                          })()}
                        </div>
                        {/* Iter88zbw — Niederlassungs-Zuordnung nur für Reps/Manager. */}
                        {["sales_rep", "sales_manager"].includes(u.role) && (
                          <div className="md:col-span-3">
                            <label className="block text-xs uppercase tracking-widest text-zinc-400 mb-1">
                              Niederlassung
                            </label>
                            <select
                              data-testid={`pending-branch-${u.id}`}
                              value={u.branch_id || ""}
                              onChange={(e) => update(u.id, { branch_id: e.target.value || null })}
                              className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 text-sm"
                            >
                              <option value="">– keine –</option>
                              {branches.filter((b) => b.active !== false).map((b) => (
                                <option key={b.id} value={b.id}>
                                  {b.name}{b.default_rep_margin_value != null ? ` · ${b.default_rep_margin_value}${b.default_rep_margin_type === "eur" ? " €" : " %"}` : ""}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div className="md:col-span-4">
                          <div className="flex gap-2">
                            <button
                              data-testid={`pending-approve-${u.id}`}
                              onClick={() => approve(u.id)}
                              className="flex-1 h-11 px-3 rounded-xl bg-[#00E575] text-[#09090B] font-bold text-sm hover:bg-[#00C964] transition flex items-center justify-center gap-1.5"
                            >
                              <UserCheck size={16} /> Freischalten
                            </button>
                            <button
                              data-testid={`pending-block-${u.id}`}
                              onClick={() => update(u.id, { status: "blocked" })}
                              className="h-11 px-3 rounded-xl bg-zinc-800 text-zinc-300 text-xs font-semibold hover:bg-red-600 hover:text-white transition"
                            >
                              Sperren
                            </button>
                            <button
                              data-testid={`pending-edit-${u.id}`}
                              onClick={() => setEditTarget(u)}
                              title="Benutzer bearbeiten"
                              className="h-11 w-11 rounded-xl bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-100 transition flex items-center justify-center"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              data-testid={`pending-delete-${u.id}`}
                              onClick={() => confirmDelete(u)}
                              title="Benutzer endgültig löschen"
                              className="h-11 w-11 rounded-xl bg-zinc-800 text-zinc-400 hover:bg-red-600 hover:text-white transition flex items-center justify-center"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ===== ALL OTHER USERS ===== */}
            {others.length > 0 && (
              <section data-testid="admin-users-section">
                <h2 className="font-display text-lg font-semibold text-zinc-300 mb-3">Alle Benutzer</h2>
                <div className="space-y-2">
                  {others.map((u) => (
                    <div key={u.id} className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4" data-testid={`admin-user-${u.id}`}>
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
                        <button
                          onClick={() => setExpandedUser(expandedUser === u.id ? null : u.id)}
                          className="md:col-span-3 min-w-0 text-left"
                          data-testid={`admin-user-toggle-${u.id}`}
                        >
                          <div className="font-medium truncate flex items-center gap-1.5">
                            {u.first_name} {u.last_name}
                            {u.is_test_account && (
                              <span
                                data-testid={`admin-test-badge-${u.id}`}
                                title="Dieses Konto ist als Test-Konto markiert"
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[12px] font-bold bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/40 uppercase tracking-wider"
                              >
                                <FlaskConical size={9} /> Test
                              </span>
                            )}
                            <span className="text-zinc-400 text-xs">
                              {expandedUser === u.id ? "▼" : "▶"}
                            </span>
                          </div>
                          <div className="text-sm text-zinc-300 truncate">{u.email}</div>
                          <div className="text-xs text-zinc-400 font-mono">{u.phone}</div>
                          {/* Iter88w: Mandanten-Chips — sofort sichtbar welchen Mandanten der User zugeordnet ist */}
                          {(u._tenant_ids || []).length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`admin-user-tenants-${u.id}`}>
                              {u._tenant_ids.slice(0, 3).map((tid) => {
                                const t = tenantMap[tid];
                                const isPrimary = tid === u.tenant_id;
                                return (
                                  <span
                                    key={tid}
                                    title={isPrimary ? "Primärer Mandant" : "Zusätzliche Membership"}
                                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-semibold uppercase tracking-wider ${
                                      isPrimary
                                        ? "bg-emerald-500/15 text-emerald-200 border border-emerald-500/40"
                                        : "bg-sky-500/15 text-sky-200 border border-sky-500/30"
                                    }`}
                                  >
                                    {(t?.slug || t?.name || tid.slice(0, 6)).toString().toUpperCase()}
                                  </span>
                                );
                              })}
                              {(u._tenant_ids || []).length > 3 && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-semibold text-zinc-400 bg-zinc-800/60">
                                  +{u._tenant_ids.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                        </button>

                        <div className="md:col-span-3">
                          <label className="block text-xs uppercase tracking-widest text-zinc-400 mb-1">Rolle</label>
                          <select
                            data-testid={`admin-user-role-${u.id}`}
                            value={u.role}
                            onChange={(e) => update(u.id, { role: e.target.value })}
                            className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 text-sm"
                          >
                            {ROLE_OPTIONS.map((k) => <option key={k} value={k}>{ROLE_LABEL[k]}</option>)}
                          </select>
                        </div>

                        <div className="md:col-span-3">
                          {(() => {
                            const isAgent = u.role === "agent";
                            const isRep   = u.role === "sales_rep";
                            const isMgr   = u.role === "sales_manager";
                            const isTsd   = u.role === "telesales_director";
                            const enabled = isAgent || isRep || isMgr || isTsd;
                            const label =
                              isTsd   ? "Vorgesetzter"      :
                              isMgr   ? "Vertriebsdirektor" :
                              isAgent ? "Vorgesetzter"      : "Vertriebsleiter";
                            const fieldKey =
                              isMgr || isTsd ? "sales_director_id" : "sales_manager_id";
                            const optList =
                              isTsd   ? tsdSupervisors   :
                              isMgr   ? directors        :
                              isAgent ? agentSupervisors : managers;
                            return (
                              <>
                                <label className="block text-xs uppercase tracking-widest text-zinc-400 mb-1">
                                  {label}
                                </label>
                                <select
                                  data-testid={`admin-user-manager-${u.id}`}
                                  value={u[fieldKey] || ""}
                                  onChange={(e) => update(u.id, { [fieldKey]: e.target.value || null })}
                                  className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 text-sm"
                                  disabled={!enabled}
                                >
                                  <option value="">– kein –</option>
                                  {optList.map((m) => (
                                    <option key={m.id} value={m.id}>
                                      {m.first_name} {m.last_name}{(isAgent || isTsd) ? ` · ${m.role}` : ""}
                                    </option>
                                  ))}
                                </select>
                              </>
                            );
                          })()}
                        </div>
                        {/* Iter88zbw — Niederlassung (Approved-Row). */}
                        {["sales_rep", "sales_manager"].includes(u.role) && (
                          <div className="md:col-span-3">
                            <label className="block text-xs uppercase tracking-widest text-zinc-400 mb-1">
                              Niederlassung
                            </label>
                            <select
                              data-testid={`admin-user-branch-${u.id}`}
                              value={u.branch_id || ""}
                              onChange={(e) => update(u.id, { branch_id: e.target.value || null })}
                              className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 text-sm"
                            >
                              <option value="">– keine –</option>
                              {branches.filter((b) => b.active !== false).map((b) => (
                                <option key={b.id} value={b.id}>
                                  {b.name}{b.default_rep_margin_value != null ? ` · ${b.default_rep_margin_value}${b.default_rep_margin_type === "eur" ? " €" : " %"}` : ""}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        <div className="md:col-span-3">
                          <label className="block text-xs uppercase tracking-widest text-zinc-400 mb-1">Status</label>
                          <div className="flex flex-wrap gap-1">
                            {STATUSES.map((s) => (
                              <button
                                key={s}
                                data-testid={`admin-user-status-${u.id}-${s}`}
                                onClick={() => update(u.id, { status: s })}
                                className={`flex-1 h-10 px-2 rounded-xl text-xs font-semibold uppercase tracking-wider ${
                                  u.status === s
                                    ? s === "active" ? "bg-[#00E575] text-[#09090B]"
                                    : s === "blocked" ? "bg-red-600 text-white"
                                    : "bg-amber-500 text-[#09090B]"
                                    : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
                                }`}
                              >
                                {s === "active" ? "Aktiv" : s === "blocked" ? "Sperre" : "Pend."}
                              </button>
                            ))}
                            <button
                              data-testid={`admin-user-edit-${u.id}`}
                              onClick={() => setEditTarget(u)}
                              title="Benutzer bearbeiten (Telefon, E-Mail, Name, Adresse, IBAN)"
                              className="h-10 w-10 shrink-0 rounded-xl bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-100 transition flex items-center justify-center"
                            >
                              <Pencil size={15} />
                            </button>
                            <button
                              data-testid={`admin-user-permissions-${u.id}`}
                              onClick={() => setPermsTarget(u)}
                              title="Rechte / Kacheln für diesen Nutzer anpassen"
                              className="h-10 w-10 shrink-0 rounded-xl bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 hover:text-emerald-100 transition flex items-center justify-center border border-emerald-500/30"
                            >
                              <ShieldCheck size={15} />
                            </button>
                            <button
                              data-testid={`admin-user-memberships-${u.id}`}
                              onClick={() => setMembershipsTarget(u)}
                              title="Mandanten-Zuordnungen bearbeiten (Rollen, Marge, Primary)"
                              className="h-10 w-10 shrink-0 rounded-xl bg-sky-500/20 text-sky-300 hover:bg-sky-500/30 hover:text-sky-100 transition flex items-center justify-center border border-sky-500/30"
                            >
                              <Building2 size={15} />
                            </button>
                            {/* Iter88x — Bulk-Apply: Rechte dieses Users auf alle seine Kinder übertragen */}
                            {(u.role === "sales_manager" || u.role === "sales_director" || u.role === "telesales_director") && (
                              <button
                                data-testid={`admin-user-bulk-${u.id}`}
                                onClick={() => setBulkTarget(u)}
                                title="Rechte auf alle Team-Mitglieder anwenden (Bulk)"
                                className="h-10 w-10 shrink-0 rounded-xl bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 hover:text-amber-100 transition flex items-center justify-center border border-amber-500/30"
                              >
                                <Users size={15} />
                              </button>
                            )}
                            <button
                              data-testid={`admin-user-delete-${u.id}`}
                              onClick={() => confirmDelete(u)}
                              title="Benutzer endgültig löschen"
                              className="h-10 w-10 shrink-0 rounded-xl bg-zinc-800 text-zinc-400 hover:bg-red-600 hover:text-white transition flex items-center justify-center"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Expanded profile details */}
                      {expandedUser === u.id && (
                        <div data-testid={`admin-user-details-${u.id}`} className="mt-4 pt-4 border-t border-zinc-800 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <div className="text-xs uppercase tracking-widest text-zinc-400 mb-1">Adresse</div>
                            <div className="text-zinc-200 leading-snug">
                              {u.street ? <>{u.street} {u.house_number}<br />{u.zip} {u.city}</> : <span className="text-zinc-500 italic">nicht hinterlegt</span>}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs uppercase tracking-widest text-zinc-400 mb-1">Bankverbindung</div>
                            <div className="text-zinc-200 leading-snug">
                              {u.iban ? <span className="font-mono">{u.iban}</span> : <span className="text-zinc-500 italic">nicht hinterlegt</span>}
                              {u.bic && <><br /><span className="text-zinc-400">BIC:</span> <span className="font-mono">{u.bic}</span></>}
                              {u.account_holder && <><br /><span className="text-zinc-400">Kto:</span> {u.account_holder}</>}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs uppercase tracking-widest text-zinc-400 mb-1">Geschäftlich</div>
                            <div className="text-zinc-200 leading-snug">
                              {u.company_name || <span className="text-zinc-500 italic">—</span>}
                              {u.vat_id && <><br /><span className="text-zinc-400">USt-ID:</span> <span className="font-mono">{u.vat_id}</span></>}
                              {u.tax_number && <><br /><span className="text-zinc-400">Steuer:</span> {u.tax_number}</>}
                              {u.commercial_register && <><br /><span className="text-zinc-400">HRB:</span> {u.commercial_register}</>}
                            </div>
                          </div>
                          {u.plausibility_score !== undefined && u.plausibility_score !== null && (
                            <div className="md:col-span-3 pt-2 border-t border-zinc-800/50">
                              <span className="text-xs uppercase tracking-widest text-zinc-400">Plausibilität:</span>{" "}
                              <span className={`font-bold ${u.plausibility_score >= 100 ? "text-[#00E575]" : u.plausibility_score >= 80 ? "text-amber-400" : "text-red-400"}`}>
                                {u.plausibility_score}/100
                              </span>
                              {u.auto_activated_at && <span className="text-xs text-zinc-400 ml-2">• auto-freigeschaltet am {new Date(u.auto_activated_at).toLocaleDateString("de-DE")}</span>}
                            </div>
                          )}

                          {/* ===== Rechtsdokumente (DSGVO Art. 7 Audit-Trail) ===== */}
                          <div className="md:col-span-3 pt-3 border-t border-zinc-800/50" data-testid={`admin-legal-section-${u.id}`}>
                            <div className="text-xs uppercase tracking-widest text-zinc-400 mb-1.5">
                              Rechtsdokumente · Audit-Trail
                            </div>
                            <AdminLegalAcceptanceSummary acceptances={u.legal_acceptances} />
                          </div>

                          {/* ===== Test-Account Section ===== */}
                          <div
                            data-testid={`admin-test-account-section-${u.id}`}
                            className={`md:col-span-3 mt-2 pt-3 border-t border-zinc-800/50 ${u.is_test_account ? "" : "opacity-90"}`}
                          >
                            <div className="flex flex-wrap items-start gap-3 justify-between">
                              <label className="flex items-start gap-2 cursor-pointer select-none">
                                <input
                                  type="checkbox"
                                  data-testid={`admin-test-checkbox-${u.id}`}
                                  checked={!!u.is_test_account}
                                  onChange={(e) => toggleTestAccount(u, e.target.checked)}
                                  className="mt-0.5 h-4 w-4 rounded border-zinc-600 bg-zinc-900 text-fuchsia-500 focus:ring-fuchsia-500"
                                />
                                <span>
                                  <span className="text-sm font-semibold text-zinc-100 flex items-center gap-1.5">
                                    <FlaskConical size={13} className="text-fuchsia-400" />
                                    Dies ist ein Test-Konto
                                  </span>
                                  <span className="text-xs text-zinc-400 leading-snug block mt-0.5">
                                    Markiert das Konto als Sandbox. Erlaubt den Knopf
                                    „Test-Daten zurücksetzen" — entfernt alle Projekte/Termine
                                    dieses Users, ohne das Konto selbst zu löschen.
                                  </span>
                                </span>
                              </label>
                              {u.is_test_account && (
                                <button
                                  data-testid={`admin-reset-test-data-${u.id}`}
                                  onClick={() => setResetTarget({
                                    id: u.id,
                                    name: `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email,
                                    email: u.email,
                                    phone: u.phone,
                                  })}
                                  title="Alle Daten dieses Test-Kontos entfernen (Konto bleibt erhalten)"
                                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-fuchsia-500/10 border border-fuchsia-500/40 text-fuchsia-200 text-xs font-semibold hover:bg-fuchsia-500/20 transition shrink-0"
                                >
                                  <Eraser size={13} /> Test-Daten zurücksetzen
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ===== SOFT-DELETED SECTION ===== */}
            {showDeleted && deleted.length > 0 && (
              <section data-testid="admin-deleted-section" className="mt-8">
                <div className="flex items-center gap-2 mb-3">
                  <EyeOff className="text-zinc-400" size={18} />
                  <h2 className="font-display text-lg font-semibold text-zinc-300">
                    Deaktivierte Konten ({deleted.length})
                  </h2>
                </div>
                <div className="space-y-2">
                  {deleted.map((u) => (
                    <div
                      key={u.id}
                      data-testid={`admin-deleted-user-${u.id}`}
                      className="bg-zinc-900/40 border border-zinc-800 rounded-2xl p-4 opacity-80"
                    >
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-zinc-200 truncate">{u.first_name} {u.last_name}</div>
                          <div className="text-sm text-zinc-400 truncate">{u.email}</div>
                          {u.phone && <div className="text-xs text-zinc-300 font-mono">{u.phone}</div>}
                          <div className="text-xs text-zinc-300 mt-1">
                            Deaktiviert am{" "}
                            {u.soft_deleted_at ? new Date(u.soft_deleted_at).toLocaleString("de-DE") : "—"}
                            {u.previous_status && <> · vorheriger Status: <strong className="text-zinc-400">{u.previous_status}</strong></>}
                          </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <button
                            data-testid={`admin-restore-${u.id}`}
                            onClick={() => restoreUser(u)}
                            className="h-10 px-3 rounded-xl bg-[#00E575]/10 border border-[#00E575]/40 text-[#00E575] text-sm font-semibold hover:bg-[#00E575]/20 transition inline-flex items-center gap-1.5"
                          >
                            <RotateCcw size={14} /> Wiederherstellen
                          </button>
                          <button
                            data-testid={`admin-deleted-purge-${u.id}`}
                            onClick={() => confirmDelete(u)}
                            title="Endgültig aus DB entfernen"
                            className="h-10 w-10 rounded-xl bg-zinc-800 text-zinc-400 hover:bg-red-600 hover:text-white transition flex items-center justify-center"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent
          data-testid="admin-delete-confirm-dialog"
          className="bg-[#18181B] border-red-500/40 text-zinc-100 max-w-xl"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-300">
              <AlertTriangle size={20} />
              Wie möchten Sie diesen Benutzer entfernen?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-zinc-300 leading-relaxed space-y-3">
                <div className="p-3 bg-[#09090B] border border-zinc-800 rounded-lg">
                  <div className="font-semibold text-zinc-50">{deleteTarget?.name}</div>
                  <div className="text-xs text-zinc-300 mt-0.5">{deleteTarget?.email}</div>
                  {deleteTarget?.phone && (
                    <div className="text-xs text-zinc-400 font-mono mt-0.5">{deleteTarget.phone}</div>
                  )}
                  <div className="mt-2 text-xs uppercase tracking-wider text-zinc-400">
                    Rolle: {ROLE_LABEL[deleteTarget?.role] || deleteTarget?.role} · Status: {deleteTarget?.status}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  <div className="p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/30">
                    <div className="font-bold text-amber-300 flex items-center gap-1.5 mb-1">
                      <PowerOff size={12} /> Nur deaktivieren
                    </div>
                    <div className="text-zinc-300 leading-snug">
                      Konto wird ausgeblendet, Daten bleiben erhalten und können wiederhergestellt werden. <strong className="text-amber-200">Empfohlen.</strong>
                    </div>
                  </div>
                  <div className="p-2.5 rounded-lg bg-red-500/5 border border-red-500/30">
                    <div className="font-bold text-red-300 flex items-center gap-1.5 mb-1">
                      <Trash2 size={12} /> Endgültig löschen
                    </div>
                    <div className="text-zinc-300 leading-snug">
                      Alle Daten werden <strong className="text-red-200">unwiderruflich</strong> gelöscht. Keine Wiederherstellung möglich.
                    </div>
                  </div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-2">
            <AlertDialogCancel
              data-testid="admin-delete-cancel-btn"
              className="bg-zinc-800 text-zinc-200 border-zinc-700 hover:bg-zinc-700 mt-0"
            >
              Abbrechen
            </AlertDialogCancel>
            <button
              type="button"
              data-testid="admin-delete-hard-btn"
              onClick={performDelete}
              className="inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-md bg-red-600/90 text-white text-sm font-semibold hover:bg-red-700 transition"
            >
              <Trash2 size={14} /> Endgültig löschen
            </button>
            <AlertDialogAction
              data-testid="admin-delete-soft-btn"
              onClick={performSoftDelete}
              className="bg-amber-500 text-[#09090B] hover:bg-amber-400 font-bold"
            >
              <PowerOff size={14} className="mr-1.5" /> Nur deaktivieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset test-data confirmation dialog */}
      <AlertDialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <AlertDialogContent
          data-testid="admin-reset-confirm-dialog"
          className="bg-[#18181B] border-fuchsia-500/40 text-zinc-100 max-w-lg"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-fuchsia-300">
              <Eraser size={20} />
              Test-Daten dieses Kontos zurücksetzen?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-zinc-300 leading-relaxed space-y-3">
                <div className="p-3 bg-[#09090B] border border-zinc-800 rounded-lg">
                  <div className="font-semibold text-zinc-50">{resetTarget?.name}</div>
                  {resetTarget?.email && <div className="text-xs text-zinc-300 mt-0.5">{resetTarget.email}</div>}
                  {resetTarget?.phone && (
                    <div className="text-xs text-zinc-400 font-mono mt-0.5">{resetTarget.phone}</div>
                  )}
                </div>
                <p>
                  Alle <strong className="text-zinc-100">Projekte, Termine, Aufgaben, Nachrichten,
                  Fotos, Skizzen und Quiz-Ergebnisse</strong> dieses Test-Kontos werden{" "}
                  <strong className="text-red-300">unwiderruflich gelöscht</strong>.
                </p>
                <p className="text-xs text-zinc-300 font-medium">
                  Das Konto selbst bleibt erhalten — der Tester kann sich anschließend wieder
                  einloggen und beginnt mit einer leeren Sandbox.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-2">
            <AlertDialogCancel
              data-testid="admin-reset-cancel-btn"
              className="bg-zinc-800 text-zinc-200 border-zinc-700 hover:bg-zinc-700 mt-0"
            >
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-reset-confirm-btn"
              onClick={performResetTestData}
              className="bg-fuchsia-500 text-[#09090B] hover:bg-fuchsia-400 font-bold"
            >
              <Eraser size={14} className="mr-1.5" /> Jetzt zurücksetzen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Quick-Create User Modal (Admin Convenience) */}
      {creating && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={() => !createSaving && setCreating(false)} data-testid="admin-create-user-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-[#18181B] border border-zinc-700 rounded-2xl max-w-md w-full">
            <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-4">
              <div>
                <h2 className="font-display text-lg font-bold inline-flex items-center gap-2"><UserPlus size={16} className="text-[#00E575]"/> Neuer User</h2>
                <p className="text-[12px] text-zinc-400">Admin-Quick-Create — User ist sofort aktiv, kein OTP nötig</p>
              </div>
              <button onClick={() => setCreating(false)} disabled={createSaving} className="h-8 w-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 inline-flex items-center justify-center"><XIcon size={14}/></button>
            </div>
            <div className="p-5 space-y-3">
              {needsTenantPick && (
                <div data-testid="create-user-tenant-wrapper" className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
                  <label className="text-[12px] uppercase tracking-widest text-amber-300 mb-1 font-bold flex items-center gap-1.5">
                    <Building2 size={12} /> Mandant *
                  </label>
                  <select
                    value={createForm.tenant_id}
                    onChange={(e) => setCreateForm({ ...createForm, tenant_id: e.target.value })}
                    data-testid="create-user-tenant"
                    className="h-10 w-full bg-[#09090B] border border-amber-500/40 rounded-lg px-3 text-zinc-50 focus:border-amber-400 outline-none text-[13px]"
                  >
                    <option value="">— Mandant auswählen —</option>
                    {tenantOptions.map((t) => (
                      <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>
                    ))}
                  </select>
                  <p className="text-[10.5px] text-amber-200/80 mt-1 leading-snug">
                    Sie sind im <strong>Global-Modus</strong>. Bitte wählen Sie den Mandanten, dem der neue User zugeordnet werden soll.
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Vorname *</label>
                  <input value={createForm.first_name} onChange={(e) => setCreateForm({...createForm, first_name: e.target.value})} data-testid="create-user-first-name" className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium" />
                </div>
                <div>
                  <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Nachname *</label>
                  <input value={createForm.last_name} onChange={(e) => setCreateForm({...createForm, last_name: e.target.value})} data-testid="create-user-last-name" className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium" />
                </div>
              </div>
              <div>
                <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">E-Mail *</label>
                <input type="email" value={createForm.email} onChange={(e) => setCreateForm({...createForm, email: e.target.value})} data-testid="create-user-email" placeholder="agent@firma.de" className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium" />
              </div>
              <div>
                <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Telefon * (für OTP-Login)</label>
                <input type="tel" value={createForm.phone} onChange={(e) => setCreateForm({...createForm, phone: e.target.value})} data-testid="create-user-phone" placeholder="+4915XXXXXXXX" className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium" />
              </div>
              <div>
                <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Rolle *</label>
                <select value={createForm.role} onChange={(e) => setCreateForm({...createForm, role: e.target.value})} data-testid="create-user-role" className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium">
                  {allowedRoles.map(r => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
                </select>
                <p className="text-[10.5px] text-zinc-500 mt-1">Bei „Telesales-Agent": Login dann via /auth → Telesales freigeschaltet.</p>
              </div>
            </div>
            <div className="border-t border-zinc-800 px-5 py-3 flex justify-end gap-2">
              <button onClick={() => setCreating(false)} disabled={createSaving} className="h-9 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[12px] text-zinc-200 font-medium">Abbrechen</button>
              <button onClick={submitCreate} disabled={createSaving} data-testid="create-user-submit" className="h-9 px-4 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-[#00C964] disabled:opacity-50">
                {createSaving ? <Loader2Icon size={13} className="animate-spin"/> : <SaveIcon size={13}/>}
                Anlegen
              </button>
            </div>
          </div>
        </div>
      )}

      {inviteOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-3" data-testid="admin-invite-user-modal">
          <div className="w-full max-w-md bg-[#18181B] border-t sm:border border-zinc-800 sm:rounded-2xl">
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
              <h3 className="font-bold flex items-center gap-2"><MailIcon size={16}/> User einladen</h3>
              <button onClick={() => setInviteOpen(false)} className="p-1 hover:bg-zinc-800 rounded">
                <XIcon size={16}/>
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-[12px] text-zinc-300 font-medium">Der User bekommt einen Magic-Link per E-Mail. Er füllt seine Daten selbst ein.</p>
              {needsTenantPick && (
                <div data-testid="invite-tenant-picker-wrap">
                  <label className="block text-[12px] uppercase tracking-widest text-amber-400 mb-1 font-bold">Mandant *</label>
                  <select
                    value={inviteForm.tenant_id}
                    onChange={(e) => setInviteForm({ ...inviteForm, tenant_id: e.target.value })}
                    data-testid="invite-tenant"
                    className="h-10 w-full bg-[#09090B] border border-amber-700/60 rounded-lg px-3 text-zinc-50 focus:border-amber-500 outline-none text-[13px]"
                  >
                    <option value="">— Mandant auswählen —</option>
                    {tenantOptions.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  <p className="text-[12px] text-zinc-300 mt-1">Sie sind als Super-Admin im Global-Modus — wählen Sie den Ziel-Mandanten.</p>
                </div>
              )}
              <div>
                <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Rolle *</label>
                <select value={inviteForm.role} onChange={(e) => setInviteForm({...inviteForm, role: e.target.value})} data-testid="invite-role" className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium">
                  {allowedRoles.map(r => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">E-Mail (Empfänger des Links) *</label>
                <input type="email" value={inviteForm.target_email} onChange={(e) => setInviteForm({...inviteForm, target_email: e.target.value})} data-testid="invite-email" className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium"/>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Vorname</label>
                  <input value={inviteForm.first_name} onChange={(e) => setInviteForm({...inviteForm, first_name: e.target.value})} data-testid="invite-first" className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium"/>
                </div>
                <div>
                  <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Nachname</label>
                  <input value={inviteForm.last_name} onChange={(e) => setInviteForm({...inviteForm, last_name: e.target.value})} data-testid="invite-last" className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium"/>
                </div>
              </div>
              <button onClick={submitInvite} disabled={inviteSaving} data-testid="invite-submit" className="w-full h-11 rounded-lg bg-amber-500 hover:bg-amber-600 text-[#09090B] font-bold text-[12px] inline-flex items-center justify-center gap-1.5 disabled:bg-zinc-800 disabled:text-zinc-300">
                {inviteSaving ? <Loader2Icon size={13} className="animate-spin"/> : <><MailIcon size={13}/> Einladung senden</>}
              </button>
              {lastInviteUrl && (
                <div className="p-3 rounded-lg bg-[#00E575]/5 border border-[#00E575]/20" data-testid="invite-link-display">
                  <div className="text-[12px] uppercase tracking-widest text-[#00E575] font-bold mb-1">Einladungs-Link</div>
                  <input readOnly value={lastInviteUrl} onFocus={(e) => e.target.select()} className="w-full bg-[#09090B] border border-zinc-700 rounded px-2 py-1.5 text-[12px] text-zinc-300 font-mono"/>
                  <p className="text-[12px] text-zinc-300 mt-1">Können Sie auch manuell kopieren und weiterleiten (z.B. via WhatsApp).</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {editTarget && (
        <AdminEditUserModal
          user={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); }}
        />
      )}
      {permsTarget && (
        <PermissionsEditor
          userId={permsTarget.id}
          userName={`${permsTarget.first_name || ""} ${permsTarget.last_name || ""}`.trim() || permsTarget.email || permsTarget.phone}
          userRole={permsTarget.role}
          onClose={() => setPermsTarget(null)}
        />
      )}
      {membershipsTarget && (
        <MembershipDialog
          userId={membershipsTarget.id}
          onClose={() => setMembershipsTarget(null)}
        />
      )}
      {bulkTarget && (
        <BulkApplyDialog
          manager={bulkTarget}
          onClose={() => setBulkTarget(null)}
          onApplied={() => { setBulkTarget(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Iter88x — Bulk-Rechte-Übertragung
// ---------------------------------------------------------------------------
function BulkApplyDialog({ manager, onClose, onApplied }) {
  const [preset, setPreset] = useState("telesales_innendienst");
  const [roleFilter, setRoleFilter] = useState("agent");
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const PRESETS = [
    { key: "telesales_innendienst", label: "Telesales Innendienst", caps: ["create_project", "edit_own_project", "create_orientation_offer"] },
    { key: "solo_vertrieb", label: "Nur eigene Kunden", caps: ["create_project", "edit_own_project", "create_orientation_offer", "convert_orientation_offer", "edit_offer", "send_offer_pdf"] },
    { key: "aussendienst", label: "Außendienst", caps: ["create_project", "edit_own_project", "create_orientation_offer", "convert_orientation_offer", "edit_offer", "send_offer_pdf"] },
    { key: "vertriebsleiter", label: "Vertriebsleiter", caps: ["create_project", "edit_own_project", "see_all_projects", "create_orientation_offer", "convert_orientation_offer", "edit_offer", "send_offer_pdf", "override_margin_per_offer", "see_team_analytics", "handover_intake_project", "see_intake_pipeline", "give_discount_above_cap"] },
    { key: "read_only", label: "Read-Only Management", caps: ["see_all_projects", "see_team_analytics", "see_intake_pipeline"] },
  ];

  const apply = async () => {
    const p = PRESETS.find((x) => x.key === preset);
    if (!p) return;
    if (!window.confirm(`Wirklich Preset „${p.label}" auf ALLE ${roleFilter || "Team-Mitglieder"} von ${manager.first_name} ${manager.last_name} übertragen?`)) return;
    setApplying(true);
    try {
      const r = await api.post("/admin/permissions/bulk-apply", {
        preset_caps: p.caps,
        target_manager_id: manager.id,
        target_role: roleFilter || undefined,
      });
      setResult(r.data);
      toast.success(`${r.data?.updated || 0} Nutzer aktualisiert.`);
      if ((r.data?.updated || 0) > 0) onApplied?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk-Anwendung fehlgeschlagen");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !applying && onClose?.()}>
      <div className="bg-[#101015] ring-1 ring-zinc-800 rounded-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="bulk-apply-dialog">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-2">
          <Users size={16} className="text-amber-300" />
          <div className="flex-1">
            <div className="text-[15px] font-bold text-zinc-100">Rechte-Bulk-Vergabe</div>
            <div className="text-[12px] text-zinc-400">an Team von {manager.first_name} {manager.last_name}</div>
          </div>
          <button onClick={onClose} disabled={applying} className="w-8 h-8 rounded-lg hover:bg-zinc-800 text-zinc-400"><XIcon size={16}/></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-zinc-400 mb-1.5">Preset</label>
            <select
              data-testid="bulk-apply-preset"
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
              disabled={applying}
              className="h-10 w-full rounded-lg bg-[#09090B] border border-zinc-700 px-3 text-zinc-100 text-[13px] focus:border-amber-400 outline-none"
            >
              {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
            <p className="text-[11px] text-zinc-500 mt-1">
              Erhält: {(PRESETS.find((p) => p.key === preset)?.caps || []).join(", ") || "—"}
            </p>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-zinc-400 mb-1.5">Rollen-Filter</label>
            <select
              data-testid="bulk-apply-role"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              disabled={applying}
              className="h-10 w-full rounded-lg bg-[#09090B] border border-zinc-700 px-3 text-zinc-100 text-[13px] focus:border-amber-400 outline-none"
            >
              <option value="">Alle Team-Mitglieder</option>
              <option value="agent">Nur Agents (Telesales)</option>
              <option value="sales_rep">Nur Sales-Reps (Außendienst)</option>
            </select>
          </div>
          {result && (
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3 text-[12px] text-emerald-100">
              <div className="font-semibold mb-1">{result.updated} Nutzer aktualisiert:</div>
              <ul className="list-disc pl-5 space-y-0.5">
                {(result.children || []).slice(0, 10).map((c) => (
                  <li key={c.id}>{c.name} <span className="text-emerald-300/70">({c.role})</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2">
          <button onClick={onClose} disabled={applying} className="h-10 px-4 rounded-lg text-[13px] font-semibold text-zinc-300 hover:bg-zinc-800">Schließen</button>
          <button
            data-testid="bulk-apply-confirm"
            onClick={apply}
            disabled={applying}
            className="h-10 px-4 rounded-lg text-[13px] font-bold bg-amber-500 text-[#09090B] hover:bg-amber-400 inline-flex items-center gap-2 disabled:opacity-60"
          >
            {applying ? <Loader2Icon size={13} className="animate-spin"/> : <Users size={13}/>}
            Übertragen
          </button>
        </div>
      </div>
    </div>
  );
}
